<template>
  <div>
    <div class="row">
      <affix class="col-md-4 vue-affix"
             :enabled="(windowWidth > 991)"
             relative-element-selector="#form-builder-content"
             :offset="{ top: 90, bottom: 0 }">
        <div class="portlet light">
          <div class="portlet-title tabbable-line">
            <div class="caption">
              <i class="icon-settings font-dark"></i>
              <span class="caption-subject font-dark sbold uppercase">Editing</span>
            </div>

            <ul v-if="!loading" class="nav nav-tabs">
              <li :class="{active: activeTab === 'fields'}" @click="setActiveTab('fields')">
                <a href="#form-sidebar-elements" data-toggle="tab" aria-expanded="true"> Fields </a>
              </li>
              <li :class="{active: activeTab === 'conditions'}" @click="setActiveTab('conditions')">
                <a href="#form-sidebar-conditions" data-toggle="tab" aria-expanded="true">
                  Conditions </a>
              </li>
              <li :class="{active: activeTab === 'settings'}" @click="setActiveTab('settings')">
                <a href="#form-sidebar-settings" data-toggle="tab" aria-expanded="false"> Settings </a>
              </li>
            </ul>
          </div>
          <spinner v-if="loading"/>
          <div class="tab-content form-sidebar-tab" v-if="!loading">
            <div class="tab-pane vb" :class="{active: activeTab === 'fields'}" id="form-sidebar-elements" v-bar>
              <div>
                <referral-add-field-from-profile
                  v-if="module.role === 'referral'"
                  :schema="schema"
                  @update-form="updateForm"
                />
                <vue-form-builder-field-list
                  :include-destination-account-field="includeDestinationAccountField"
                  :include-programs-checkbox-group-field="includeProgramsCheckboxGroupField"
                  @shared-field-added="sharedFields[$event] = {};"
                  @drag-started="openSettingsTab"
                />
              </div>
            </div>
            <div class="tab-pane vb" :class="{active: activeTab === 'conditions'}" id="form-sidebar-conditions" v-bar>
              <div>
                <vue-form-conditions-editor
                  :schema="schema"
                  :flat-schema="flatSchema"
                  :conditions="conditions"
                  :validation-conditions="validationConditions"
                  :hide-values="hideValues"
                  :calculations="calculations"
                  :update-conditions="updateConditions"
                />
                <vue-program-status-conditions
                  ref="programStatusConditions"
                  v-if="module.key === 'participants_assignment'"
                  v-model="systemConditions.programStatus"
                  :schema="flatFormItems"
                />
              </div>
            </div>

            <div class="tab-pane vb" :class="{active: activeTab === 'settings'}" id="form-sidebar-settings" v-bar>
              <div>
                <form data-vv-scope="settings_form">
                  <div class="form-group" :class="{ 'has-error': errors.has('settings_form.Form Name') }">
                    <label for="form-name">Form Name</label>
                    <input id="form-name" class="form-control"
                           name="Form Name"
                           type="text"
                           v-model="name"
                           v-validate="'required'">
                    <span v-show="errors.has('settings_form.Form Name')"
                          class="help-block">{{ errors.first('settings_form.Form Name') }}</span>
                  </div>
                  <div class="form-group"
                       :class="{ 'has-error': errors.has('settings_form.Description') }">
                    <label for="form-desc">Description</label>
                    <textarea id="form-desc" class="form-control" name="Description" type="text"
                              v-model="description"
                              v-validate="'required'"></textarea>
                    <span v-show="errors.has('settings_form.Description')"
                          class="help-block">{{ errors.first('settings_form.Description') }}</span>
                  </div>

                  <div class="form-group" v-if="type == 'form' && !this.lockModule && this.module.role != 'referral'"
                       :class="{ 'has-error': errors.has('settings_form.Related to') }">
                    <label>Related to</label>
                    <select class="form-control" v-model="moduleKey" name="Related to" v-validate="'required'">
                      <option v-for="module in modulesList" :value="module.key">{{ module.name }}</option>
                    </select>
                    <span v-show="errors.has('settings_form.Related to')"
                          class="help-block">{{ errors.first('settings_form.Related to') }}</span>
                  </div>
                  <div
                    v-if="['organization_organization','organization_general','assessment_outcomes','activities_services'].includes(module.key)"
                    class="form-group"
                    :class="{ 'has-error': errors.has('settings_form.access_level') }">
                    <label>User access</label>
                    <select class="form-control" v-model="accessLevel" name="Access level"
                            data-vv-as="Access Level"
                            data-vv-name="access_level"
                            v-validate="'required'">
                      <option v-for="accessLevel in accessLevels" :value="accessLevel.key">{{ accessLevel.name }}</option>
                    </select>
                    <span v-show="errors.has('settings_form.access_level')"
                          class="help-block">{{ errors.first('settings_form.access_level') }}</span>
                  </div>

                  <div v-if="module.role === 'referral'" class="form-group"
                       :class="{ 'has-error': errors.has('settings_form.referral_email_contact')}">
                    <div>
                      <label for="referral_email_contact">Email Address (Referral Contact, Status Notification)</label>
                    </div>
                    <select class="form-control" :name="'referral_email_contact'"
                            v-validate="'required'"
                            :data-vv-as="'Email Address'"
                            v-model="referralDestinationEmail"
                            id="referral_email_contact"
                    >
                      <option :value="null" selected disabled hidden></option>
                      <option v-for="field in flatFormItems" :value="field.name"
                              v-if="['email'].includes(field.type)">
                        {{ field.description }}
                      </option>
                    </select>
                    <span v-show="errors.has('settings_form.referral_email_contact')"
                          class="help-block">{{ errors.first('settings_form.referral_email_contact') }}</span>
                  </div>

                  <div v-if="displayInOrganizationsSelect"
                       class="form-group"
                       :class="{ 'has-error': errors.has('settings_form.form_accounts') }">
                    <label v-if="module.role === 'referral'">Display Following Organizations</label>
                    <label v-else>Display in following organizations</label>

                    <vue-select
                      v-model="accounts"
                      :options="currentOrganization.accountType === 'parent' ? childrenAccounts : userAccounts"
                      label="organizationName"
                      value="accounts"
                      :multiple=true
                      v-on:input="onDisplayInOrganizationsInput"
                      v-validate:accounts="'accounts_arr|required'"
                      maxHeight="300px"
                      :data-vv-name="'form_accounts'"
                    >
                      <template slot="option" slot-scope="option">
                        {{ option.organizationName }}
                      </template>
                    </vue-select>

                    <span v-show="errors.has('settings_form.form_accounts')"
                          class="help-block">{{ errors.first('settings_form.form_accounts') }}</span>
                  </div>

                  <div class="form-group"
                       :class="{ 'has-error': errors.has('settings_form.display_in_programs') }"
                       v-if="currentOrganization.accountType === 'program' && ['assessment_outcomes','activities_services'].includes(module.key)"
                  >
                    <label>Display in following Programs</label>

                    <vue-select
                      v-model="formPrograms"
                      :options="activeAvailablePrograms"
                      label="name"
                      value="id"
                      :multiple=true
                      maxHeight="300px"
                    >
                      <template slot="option" slot-scope="option">
                        {{ option.name }}
                      </template>
                    </vue-select>
                  </div>

                  <div class="form-group"
                       :class="{ 'has-error': errors.has('settings_form.multiple_entries') }"
                       v-if="['assessment_outcomes','activities_services','organization_organization','organization_general'].includes(module.key)"
                  >
                    <label>Numbers of Entries Allowed</label>
                    <select class="form-control" v-model="multipleEntries" name="Multiple entries"
                            v-validate="'required'">
                      <option v-bind:value="false">One entry</option>
                      <option v-bind:value="true">Multiple entries</option>
                    </select>
                    <span v-show="errors.has('settings_form.multipleEntries')"
                          class="help-block">{{ errors.first('settings_form.multipleEntries') }}</span>
                  </div>
                  <div class="btn-group spacing-button">
                    <button class="btn green dropdown-toggle" type="button"
                            data-toggle="dropdown" aria-expanded="false"> Add spacing
                      <i class="fa fa-angle-down"></i>
                    </button>
                    <ul class="dropdown-menu" role="menu">
                      <li>
                        <a @click.prevent="addSpacing(['row'])">
                          <i class="fa fa-arrows-h"></i>
                          Add spacing after each row
                        </a>
                      </li>
                      <li>
                        <a @click.prevent="addSpacing(['divider'])">
                          <i class="fa fa-window-minimize" aria-hidden="true"></i>
                          Add divider after each row
                        </a>
                      </li>
                      <li>
                        <a @click.prevent="addSpacing(['divider', 'row'])">
                          <i class="fa fa-arrows-h" aria-hidden="true"></i>
                          Add divider and spacing after each row
                        </a>
                      </li>
                      <li>
                        <a @click.prevent="addSpacing([])">
                          <i class="fa fa-times" aria-hidden="true"></i>
                          Remove all spacing and dividers
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div class="form-group">
                    <label class="margin-bottom-0">Make this form globally available</label>
                    <toggle-button :class="'margin-bottom-0'" :sync="true" :labels="true" v-model="isGlobal"/>
                  </div>

                  <div class="form-group">
                    <label class="margin-bottom-0">Share fields from this form</label>
                    <toggle-button
                      :class="'margin-bottom-0'"
                      :sync="true"
                      :labels="true"
                      v-model="hasSharedFields"
                      @change="hasSharedFields && openShareFieldsModal()"
                    />
                    <button v-if="hasSharedFields"
                            type="button"
                            class="btn btn-xs green shared-settings-btn"
                            @click="openShareFieldsModal">
                      Edit
                    </button>
                  </div>

                  <div class="form-group" v-if="['activities_services','assessment_outcomes'].includes(module.role)">
                    <label class="margin-bottom-0">Share with {{ participantSingularName }}</label>
                    <toggle-button
                      :class="'margin-bottom-0'"
                      :sync="true"
                      :labels="true"
                      v-model="shareWithParticipant"
                      @change="shareWithParticipant && openShareWithParticipantModal()"
                    />
                    <button v-if="shareWithParticipant"
                            type="button"
                            class="btn btn-xs green shared-settings-btn"
                            @click="openShareWithParticipantModal">
                      Edit
                    </button>
                  </div>

                  <div class="form-group" v-if="['referral'].includes(module.role)">
                    <label class="margin-bottom-0">Enable reCaptcha</label>
                    <toggle-button :class="'margin-bottom-0'" :sync="true" :labels="true" v-model="captchaEnabled"/>
                  </div>

                </form>
                <vue-map-data
                  v-if="module.role !== 'referral'"
                  ref="mapData"
                  :flat-form-items="flatFormItems"
                  :is-form="type === 'form'"
                  :is-program="currentOrganization.accountType === 'program'"
                  :custom-columns-map="customColumnsMap"
                  :columns-map="module.columns_map"
                  :fields-map="columnsMap"
                  :allow-adding-custom-fields="['participants_profile', 'members_profile', 'participants_assignment'].includes(this.module.key)"
                  @update="updateMapData"
                />
              </div>
            </div>
          </div>
          <div class="form-group" v-if="!loading">
            <button class="btn green mt-ladda-btn ladda-button save-form-btn" ref="saveFormBtn"
                    @click.prevent="saveChanges" data-style="expand-left" id="save-form-button" v-if="!isFormLocked">
              <span class="ladda-label">Save Changes</span>
            </button>
            <button
              v-if="!isFormLocked && type === 'form' && (!(['profile','assignment'].includes(module.role)) || !formPublish)"
              class="btn green" @click.prevent="publishForm(false)">
              {{ publishText }}
            </button>
            <router-link to="/admin/form-builder" tag="button" class="btn default" v-if="!isFormLocked">
              Cancel
            </router-link>
          </div>
        </div>
      </affix>
      <div class="col-md-8" id="form-builder-content">
        <div class="portlet light">
          <div class="portlet-title tabbable-line">
            <div class="caption">
              <i class="icon-settings font-dark"></i>
              <span class="caption-subject font-dark sbold uppercase">Form Builder</span>
            </div>
            <ul v-if="!loading" class="nav nav-tabs">
              <li @click.prevent="lockForm">
                <i class="fa fa-lock" aria-hidden="true" v-if="isFormLocked"></i>
                <i class="fa fa-unlock-alt" aria-hidden="true" v-else></i>
              </li>
              <li @click="duplicateForm">
                <i class="fa fa-files-o" aria-hidden="true"></i>
              </li>
              <li @click="expandForm">
                <i class="fa fa-minus-square-o" aria-hidden="true" v-if="isExpand"></i>
                <i class="fa fa-plus-square-o" aria-hidden="true" v-else=""></i>
              </li>
              <li @click="formRemove" v-if="!isFormLocked && (appUser.access == 6 || module.group != 'core')"><i
                class="fa fa-trash" aria-hidden="true"></i></li>
            </ul>
            <ul v-if="!loading" class="nav nav-tabs">
              <li class="active">
                <a href="#form-settings" data-toggle="tab" aria-expanded="true"> Settings </a>
              </li>
              <li @click="renderForm">
                <a href="#form_preview" data-toggle="tab" aria-expanded="false"> Preview </a>
              </li>
              <li v-if="shareWithParticipant" @click="renderForm">
                <a href="#form_preview_participant" data-toggle="tab" aria-expanded="false"> {{ participantSingularName }} Preview </a>
              </li>
              <template v-if="module.role === 'referral'">
                <li>
                  <a href="#form_share" data-toggle="tab" aria-expanded="false"> Share </a>
                </li>
                <li>
                  <a href="#map-profile-form" data-toggle="tab" aria-expanded="false"> Map Profile Form</a>
                </li>
                <li>
                  <a href="#emails" data-toggle="tab" aria-expanded="false"> Emails</a>
                </li>
                <li>
                  <a href="#confirmation" data-toggle="tab" aria-expanded="false"> Confirmation</a>
                </li>
              </template>
            </ul>
          </div>

          <div class="tab-content">
            <spinner v-if="loading"/>
            <div v-if="!loading" class="tab-pane active" id="form-settings">
              <vue-form-editor :schema="schema"
                               :shared-fields="sharedFields"
                               :share-with-participant="shareWithParticipant"
                               :id="id"
                               :key="'main-editor'"
                               @field-removed="onFieldRemoved"
              />
            </div>

            <div v-if="!loading" class="tab-pane" id="form_preview">
              <vue-form-render :schema="schema"
                               :conditions="conditions"
                               :update-conditions="updateConditions"
                               :calculations="calculations"
                               :systemConditions="systemConditions"
                               :validation-conditions="validationConditions"
                               :hideValues="hideValues"
                               :date-format="appUser.date_format"
                               :user-access-level="appUser.access"
                               :referral-destination-accounts="referralDestinationAccounts"
                               :programs="activeAvailablePrograms"
                               :form-previews="formPreviews"
              ></vue-form-render>
            </div>

            <div v-if="!loading && shareWithParticipant" class="tab-pane" id="form_preview_participant">
              <vue-form-render :schema="schema"
                               :conditions="conditions"
                               :update-conditions="updateConditions"
                               :calculations="calculations"
                               :systemConditions="systemConditions"
                               :validation-conditions="validationConditions"
                               :hideValues="hideValues"
                               :date-format="appUser.date_format"
                               :user-access-level="appUser.access"
                               :referral-destination-accounts="referralDestinationAccounts"
                               :programs="activeAvailablePrograms"
                               :formblock="participantSubmissionLockFields"
                               :is-participant-submission="true"
                               :form-previews="formPreviews"
              ></vue-form-render>
            </div>

            <div v-if="!loading && module.role === 'referral'" class="tab-pane" id="form_share">
              <vue-form-share-links
                :url="currentOrganization.url"
                :uid="shareUid"
              />
            </div>

            <div v-if="!loading && module.role === 'referral'" class="tab-pane" id="map-profile-form">
              <spinner v-if="systemFormsLoading"/>
              <referral-map-profile-form v-if="!systemFormsLoading"
                                         :form-fields="this.flatSchema"
                                         :fields-map-from-server="referralColumnsMap"
                                         v-on:fieldsMapUpdated="setColumnsMapReferral"
                                         v-on:profileMapValidStatusUpdated="setProfileMapValidStatus"
              />
            </div>
            <div v-if="!loading && module.role === 'referral'" class="tab-pane" id="emails">
              <referral-email-settings :form-id="id"/>
            </div>
            <div v-if="!loading && module.role === 'referral'" class="tab-pane" id="confirmation">
              <referral-confirmation :form-id="id"/>
            </div>
          </div>
        </div>
      </div>
    </div>
    <has-changed-modal
      @publish="publishForm(true)"
    />
    <share-fields-modal
      :field-list="flatFormItems"
      @share="updateSharingSettings"
    />
    <share-fields-with-participant-modal
      :field-list="flatFormItems"
      @share="updateParticipantSharingSettings"
    />
  </div>
</template>

<script>
import { computed } from 'vue';
import VueFormRender from '@/components/formbuilder/VueFormRender';
import VueFormEditor from '@/components/formbuilder/VueFormEditor';
import _ from 'lodash';
import Collapse from '@/components/Collapse';
import {HTTP, ERROR} from '@/http-common';
import VueSelect from 'vue-select'
import {mapActions, mapGetters} from 'vuex';
import ReferralMapProfileForm from "./referral/ReferralMapProfileForm";
import ReferralAddFieldFromProfile from "./referral/ReferralAddFieldFromProfile";
import ReferralConfirmation from "./referral/ReferralConfirmation";
import ReferralEmailSettings from "./referral/ReferralEmailSettings";
import Spinner from "../Spinner";

import {FIELD_TYPE} from "../../enums/fieldType";
import {getSpecificPropertyFromAllFieldsOfSpecificTypeInSchema} from "../../utils/functions";
import VueFormBuilderFieldList from "./VueFormBuilderFieldList";
import VueFormShareLinks from "./VueFormShareLinks";
import HasChangedModal from "../modals/HasChangedModal";
import VueFormConditionsEditor from "./VueFormConditionsEditor";
import VueProgramStatusConditions from "./VueProgramStatusConditions";
import VueMapData from "./VueMapData";
import ShareFieldsModal from "../modals/ShareFieldsModal";
import ShareFieldsWithParticipantModal from "../modals/ShareFieldsWithParticipantModal";
import participantDisplayName from '@/mixins/participantDisplayName';

export default {
  name: 'FormBuilder',
  components: {
    ShareFieldsWithParticipantModal,
    ShareFieldsModal,
    VueMapData,
    VueProgramStatusConditions,
    VueFormConditionsEditor,
    HasChangedModal,
    VueFormShareLinks,
    VueFormBuilderFieldList,
    VueFormEditor,
    ReferralAddFieldFromProfile,
    ReferralConfirmation,
    ReferralEmailSettings,
    VueFormRender,
    Collapse,
    ReferralMapProfileForm,
    Spinner,
    VueSelect,
  },

  mixins: [
    participantDisplayName
  ],

  inject: {
    $validator: '$validator'
  },

  props: {
    'id': {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      name: '',
      description: '',
      isFormLocked: true,
      type: 'form',
      formPublish: false,
      shareUid: null,
      accessLevel: null,
      accounts: [],
      multipleEntries: true,
      systemFields: [],
      hideValues: [],
      columnsMap: [],
      customColumnsMap: [],
      formPrograms: [],
      schema: [],
      formLoaded: false,
      formOriginData: {},

      // conditions
      conditions: [],
      validationConditions: [],
      systemConditions: {},
      calculations: [],
      updateConditions: [],

      // data required for settings etc.
      // TODO: extract this to utils
      accessLevels: [
        {
          'name': 'Volunteer',
          'key': 'VOLUNTEER',
        },
        {
          'name': 'Case Manager',
          'key': 'CASE_MANAGER',
        },
        {
          'name': 'Supervisor',
          'key': 'SUPERVISOR',
        },
        {
          'name': 'Program Administrator',
          'key': 'PROGRAM_ADMINISTRATOR'
        }
      ],
      // internal stuff
      windowWidth: 0,
      isExpand: false,
      activeTab: 'fields',
      previewFormInitial: [],
      moduleKey: null,
      shareWithParticipant: false,
      hasSharedFields: false,
      isGlobal: false,
      sharedFields: {},

      // only for referrals
      profileMapValid: false,
      captchaEnabled: false,
      formPreviews: {}
    }
  },

  computed: {
    ...mapGetters({
      userAccounts: 'getUserAccounts',
      appUser: 'getUser',
      currentOrganization: 'getOrganization',
      systemFormsLoading: 'getSystemFormsLoading',
      modules: 'getModules',
      activeAvailablePrograms: 'formBuilderEditor/getActiveAvailablePrograms',
      childrenAccounts: 'formBuilderEditor/getChildrenAccounts'
    }),
    displayInOrganizationsSelect() {
      if (this.currentOrganization.accountType === 'parent'
        && ['activities_services', 'assessment_outcomes', 'organization_general', 'organization_organization', 'members_referral', 'individuals_referral'].includes(this.module.key)) {
        return true;
      } else if (this.currentOrganization.accountType === 'default'
        && ['organization_general', 'organization_organization'].includes(this.module.key)) {
        return true;
      }

      return false;
    },
    module() {
      if (this.type === 'form' && this.modules && this.moduleKey) {
        return this.modules.find(module => module.key === this.moduleKey);
      } else {
        return {
          key: null,
          role: null
        }
      }
    },
    lockModule() {
      return this.module && (this.module.group === 'core' || this.module.role === 'contact');
    },
    hasChanged() {
      // TODO: get rid of this
      return ((this.type !== this.formOriginData.type)
        || (JSON.stringify(this.conditions) !== JSON.stringify(this.formOriginData.conditions))
        || (JSON.stringify(this.systemConditions) !== JSON.stringify(this.formOriginData.systemConditionals))
        || (JSON.stringify(this.calculations) !== JSON.stringify(this.formOriginData.calculations))
        || (this.description !== this.formOriginData.description)
        || (this.name !== this.formOriginData.name)
        || (JSON.stringify(this.schema) !== JSON.stringify(this.formOriginData.schema))
        || (JSON.stringify(this.columnsMap) !== JSON.stringify(this.formOriginData.columnsMap))
        || (JSON.stringify(this.validationConditions) !== JSON.stringify(this.formOriginData.validationConditions))
        || (this.captchaEnabled !== this.formOriginData.captchaEnabled)
      );
    },
    publishText() {
      return this.formPublish ? 'Unpublish' : 'Publish';
    },
    referralColumnsMap() {
      return this.columnsMap.filter(col => col.name !== 'destination_email');
    },
    flatFormItems() {
      let items = [];
      this.iterateFormItems(item => items.push(_.cloneDeep(item)));

      return items;
    },
    flatSchema() {
      let items = [];
      this.iterateFormItems((item, fromAccordion) => {
        const clonedItem = _.cloneDeep(item)
        clonedItem.depth = fromAccordion ? 1 : 0
        items.push(clonedItem)
      });
      return items;
    },
    referralDestinationAccounts() {
      if (this.module.role !== 'referral') {
        return [];
      }
      return this.accounts.map(item => item.organizationName);
    },
    includeProgramsCheckboxGroupField() {
      return this.currentOrganization.accountType === 'program' && this.module.role === 'profile'
    },
    includeDestinationAccountField() {
      return this.module.role === 'referral' && ['child', 'parent'].includes(this.currentOrganization.accountType)
    },
    modulesList() {
      if (['assessment_outcomes', 'activities_services'].includes(this.module.key)) {
        return this.modules.filter(item => ['assessment_outcomes', 'activities_services'].includes(item.role))
      }

      if (this.module.group === 'organization') {
        return this.modules.filter(item => (item.group === 'organization'));
      }

      return [];
    },
    participantSubmissionLockFields() {
      let lockFields = [];

      this.flatFormItems.forEach(item => {
        if (!item.hasOwnProperty('by_participant') || !item.by_participant) {
          lockFields.push(item.name);
        }
      })

      return lockFields;
    },
    referralDestinationEmail: {
      get: function () {
        let dstEmailField = this.columnsMap.find(col => col.name === 'destination_email');
        return dstEmailField ? dstEmailField.value : null;
      },
      set: function (newValue) {
        let destEmailColumn = this.columnsMap.find(col => col.name === 'destination_email');

        if (!destEmailColumn) {
          this.columnsMap.push({sourceValue: 'destination_email', name: 'destination_email', value: newValue});
        } else {
          destEmailColumn.value = newValue;
        }
      }
    },
  },

  // TODO: move this do vuex
  provide() {
    return {
      // XXX: computed is used to make this reactive
      isFormLocked: computed(() => this.isFormLocked),
      shareWithParticipant: computed(() => this.shareWithParticipant),
      hasSharedFields: computed(() => this.hasSharedFields)
    }
  },
  mounted() {
    this.init();

    this.$nextTick(() => {
      window.addEventListener('resize', this.getWindowWidth);
      this.getWindowWidth();
    });

    this.fetchSystemForms();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getWindowWidth);
  },

  methods: {
    ...mapActions({
      loadUserAccounts: 'loadUserAccounts',
      fetchSystemForms: 'fetchSystemForms',
      fetchModules: 'fetchModules',
      fetchFormsWithSharedFields: 'fetchFormsWithSharedFields',
      fetchAvailablePrograms: 'formBuilderEditor/fetchAvailablePrograms',
      fetchChildrenAccounts: 'formBuilderEditor/fetchChildrenAccounts',
    }),
    iterateFormItems(callback) {
      // iterate over all fields and call callback with each item as first argument
      // and true as second one, if the item is inside the accordion
      this.schema.forEach(item => {
        if (item.type === 'accordion') {
          item.values.forEach(subitem => {
            callback(subitem, true)
          });
        }
        callback(item, false)
      });
    },
    updateForm(fields) {
      Object.assign(this, fields)
    },
    duplicateForm() {
      this.$emit('duplicate-form');
    },
    formRemove() {
      this.$emit('remove-form', [this.id, this.name, this.description]);
    },
    openSettingsTab() {
      $("a[href='#form-settings']").trigger('click');
    },
    onFieldRemoved(fieldName){
      this.columnsMap.forEach(column => {
        if (column.value === fieldName) {
          column.value = null;
        }
      })
      this.customColumnsMap.forEach(column => {
        if (column.value === fieldName) {
          column.value = null;
        }
      })
    },
    async init() {
      this.loading = true;

      let initFunctions = [
        this.fetchModules(),
        this.fetchFormsWithSharedFields()
      ]

      if (this.currentOrganization.accountType === 'program') {
        this.fetchAvailablePrograms({accountId: this.currentOrganization.id})
      }

      await Promise.all(initFunctions).then(() => {
        HTTP.post(`form/get`, {id: this.id}).then(response => {
          ERROR(response);

          if (response.data.status === 'success') {
            let form = response.data.data;
            this.$emit('form-loaded', form);

            let systemConditionals = form.system_conditionals && form.system_conditionals !== '[]' ? JSON.parse(form.system_conditionals) : {}

            // add default conditions for program status
            if (form.module.key === 'participants_assignment' && _.isEmpty(systemConditionals)) {
              systemConditionals = {
                programStatus: {
                  field: null,
                  conditions: {}
                }
              }
            }

            this.formOriginData = {
              name: form.name,
              description: form.description,
              type: form.type,
              conditions: form.conditionals ? JSON.parse(form.conditionals) : [],
              systemConditionals: systemConditionals,
              calculations: form.calculations ? JSON.parse(form.calculations) : [],
              schema: form.data ? JSON.parse(form.data) : [],
              columnsMap: form.columns_map ? JSON.parse(form.columns_map) : [],
              validationConditions: form.extra_validation_rules ? JSON.parse(form.extra_validation_rules) : [],
              captchaEnabled: form.captcha_enabled
            };

            this.name = form.name;
            this.description = form.description;
            this.type = form.type;
            this.moduleKey = form.module.key;
            this.schema = form.data ? JSON.parse(form.data) : [];
            this.hideValues = form.hide_values ? JSON.parse(form.hide_values) : [];
            this.conditions = form.conditionals ? JSON.parse(form.conditionals) : [];
            this.updateConditions = form.update_conditionals ? JSON.parse(form.update_conditionals) : [];
            this.systemConditions = systemConditionals;
            this.calculations = form.calculations ? JSON.parse(form.calculations) : [];
            this.columnsMap = form.columns_map ? JSON.parse(form.columns_map) : [];
            this.customColumnsMap = form.custom_columns ? JSON.parse(form.custom_columns) : [];
            this.accessLevel = form.access_level;
            this.accounts = _.sortBy(form.accounts, 'organizationName');
            this.multipleEntries = form.multiple_entries;
            this.formPrograms = form.form_programs;
            this.shareUid = form.share_uid;
            this.isFormLocked = form.status;
            this.formPublish = form.publish;
            this.shareWithParticipant = form.share_with_participant;
            this.validationConditions = form.extra_validation_rules ? JSON.parse(form.extra_validation_rules) : [];
            this.captchaEnabled = form.captcha_enabled;
            this.isGlobal = form.isGlobal;
            this.hasSharedFields = form.has_shared_fields;
            this.sharedFields = form.shared_fields;
            this.formPreviews = form.form_previews;

          } else {
            this.$router.push({name: 'Form Builder Index'});
            this.$notify({type: 'error', text: response.data.message})
          }
        }).catch(err => {
          console.log(err);
        }).finally(() => {
          this.loading = false;
        })
      })
    },

    recalculateForms() {
      this.loading = true;
      HTTP.post('form/calculate', {
        id: this.id
      }).then(response => {
        this.$notify('Form saved!');
        this.init();
        this.loading = false;
      })
    },

    openShareFieldsModal() {
      $('#shared-fields-modal').modal('show');
    },

    openShareWithParticipantModal() {
      $('#shared-fields-participant-modal').modal('show');
    },

    updateSharingSettings(fields) {
      fields.forEach(({name, isShared, sharedReadOnly}) => {
        this.iterateFormItems((item) => {
          if (item.name === name) {
            item.isShared = isShared;
            item.sharedReadOnly = sharedReadOnly;
          }
        })
      })
      this.$forceUpdate();
    },

    updateParticipantSharingSettings(fields) {
      fields.forEach(({name, by_participant}) => {
        this.iterateFormItems((item) => {
          if (item.name === name) {
            item.by_participant = by_participant;
          }
        })
      })
      this.$forceUpdate();
    },

    // TODO: investigate what is this for and why it errors out
    updateSharedFields(chunk, total, sharedFieldsCount) {
      HTTP.post('form/update-shared-fields', {
        id: this.id,
        shared_fields_count: sharedFieldsCount,
        chunk: chunk,
        formsDataCount: total
      }).then(response => {
        if (response.data.data.status === 'pending') {
          this.updateSharedFields(response.data.data.chunk, total, sharedFieldsCount)
        } else {
          this.recalculateForms();
        }
      });
    },

    updateMapData(fieldData) {
      const column = this.columnsMap.find(f => f.name === fieldData.name);
      if (column) {
        column.value = fieldData.value;
      } else {
        this.columnsMap.push({name: fieldData.name, value: fieldData.value})
      }
    },
    saveForm() {
      this.button = Ladda.create(document.querySelector('#save-form-button'));
      this.button.start();
      this.loading = true;

      // add default empty description, because it may mess with shared
      const schema = this.schema.map(field => {
        if (!field.description){
          field.description = '';
        }
        return field;
      })

      HTTP.post(`form/update`, {
        id: this.id,
        name: this.name,
        description: this.description,
        data: JSON.stringify(schema),
        conditionals: JSON.stringify(this.conditions),
        update_conditionals: JSON.stringify(this.updateConditions),
        system_conditionals: JSON.stringify(this.systemConditions),
        extra_validation_rules: JSON.stringify(this.validationConditions),
        hide_values: JSON.stringify(this.hideValues),
        calculations: JSON.stringify(this.calculations),
        columns_map: JSON.stringify(this.columnsMap),
        module: this.module.key,
        type: this.type,
        access_level: this.accessLevel,
        accounts: this.accounts,
        multiple_entries: this.multipleEntries,
        custom_columns: JSON.stringify(this.customColumnsMap),
        form_programs: this.formPrograms,
        share_with_participant: this.shareWithParticipant,
        has_shared_fields: this.hasSharedFields,
        is_global: this.isGlobal,
        captcha_enabled: this.captchaEnabled,
        shared_fields: this.sharedFields
      }).then(response => {
        ERROR(response);
        if (response.data.status === 'success') {

          if (response.data.data.run_update_shared_fields) {
            this.updateSharedFields(0, response.data.data.forms_data_count, response.data.data.shared_fields_count)
          } else {
            if (response.data.data.run_calculations) {
              this.recalculateForms();
            } else {
              this.$notify(response.data.data.message);
              this.init();
            }
          }
        }
      }).catch(err => {
        this.$notify({
          type: 'error',
          text: 'An error has occurred. Please contact support@casemgrsolutions.com for assistance.'
        });
        console.log(err);
      }).finally(() => {
        this.button.stop();
      });
    },
    lockForm() {
      this.loading = true;
      HTTP.post(`form/block`, {id: this.id})
        .then(response => {
          ERROR(response);
          if (response.data.status == 'success') {
            this.isFormLocked = !this.isFormLocked;
          }
        }).catch(err => {
        console.log(err);
      }).finally(() => {
        this.loading = false;
      })
    },
    expandForm() {
      if (this.isExpand) {
        $('#form-settings .collapse-item.is-active').find('.collapsible-header').trigger('click');
      } else {
        $('#form-settings .collapse-header').each(function () {
          if ($(this).attr('aria-expanded') == 'false') {
            $(this).trigger('click');
          }
        });
      }
      this.isExpand = !this.isExpand;
    },
    addSpacing(types) {
      /*
       * Add element of given type between each row in the schema.
       */
      let results = [];
      let gridSizeSum = 0;

      this.schema
        .filter((field) => !['row', 'divider'].includes(field.type) || field.grid !== 12)
        .forEach((field, index) => {
        if (gridSizeSum + parseInt(field.grid) > 12) {
          types.forEach(type => {
            results.push({
              name: type + '-' + Date.now() + index,
              type,
              label: type.charAt(0).toUpperCase() + type.slice(1),
              grid: 12,
              fixed: false,
              required: false,
              disabled: false,
              hide: field.hide,
            })
          })
          gridSizeSum = parseInt(field.grid);
        } else {
          gridSizeSum += parseInt(field.grid);
        }
        // filter out already created rows/dividers to avoid duplication
        results.push(field)
      })

      this.schema.length = 0;
      this.schema.push(...results);
    },
    publishForm(force) {
      if (!this.formPublish && !this.module.key) {
        this.$notify({
          type: 'error',
          text: 'Related to is required.'
        });
        return;
      }

      if (!this.hasChanged || force || this.formPublish) {
        HTTP.post('form/publish', {id: this.id})
          .then(response => {
            ERROR(response);
            this.formPublish = response.data.data.publish;
            if (this.formPublish) {
              this.$notify('Form published.');
            } else {
              this.$notify('Form unpublished.');
            }
            this.$emit('change-publish', this.formPublish);
            this.isFormLocked = !!this.formPublish;
          })
          .catch(err => {
            console.log(err);
          })

      } else {
        $('#has-changed-modal').modal('show');
      }
    },
    getWindowWidth(event) {
      this.windowWidth = document.documentElement.clientWidth;
    },
    renderForm() {
      this.previewFormInitial = _.cloneDeep(this.schema);
    },
    validateFields(fieldType) {
      const validationPromises = [];
      const sharedFieldsValidateScopesArray = getSpecificPropertyFromAllFieldsOfSpecificTypeInSchema(fieldType, this.schema);
      if (sharedFieldsValidateScopesArray && sharedFieldsValidateScopesArray.length > 0) {
        sharedFieldsValidateScopesArray.map((scope) => {
          validationPromises.push(this.$validator.validateAll(scope));
        })
      }

      return Promise.all(validationPromises)
    },
    async saveChanges() {
      // Validate required fields of shared fields and shared summary fields
      const areSharedFieldsValid = await [FIELD_TYPE.SHARED_FIELD, FIELD_TYPE.SHARED_FORM_PREVIEW].map(async (fieldType) => {
        const sharedFieldsValidationResults = await this.validateFields(fieldType);
        if (sharedFieldsValidationResults.includes(false)){
          this.$notify({
            type: 'error',
            text: 'Shared elements settings required.'
          });
          return false;
        }
        return true;
      }).reduce(async (accu, current) => {
        return await accu && await current
      })
      if (!areSharedFieldsValid) return false;

      // validate referral form
      if (this.module.role === 'referral' && !this.profileMapValid) {
        $("a[href='#map-profile-form']").trigger('click');
        return;
      }

      // validate map data component
      let isMapDataValid = true;
      if (this.$refs.mapData) {
        isMapDataValid = await this.$refs.mapData.validateAll();
      }

      // validate rest of the settings
      let isRestSettingsValid = await this.$validator.validateAll('settings_form')

      if (!isMapDataValid || !isRestSettingsValid) {
        $("a[href='#form-sidebar-settings']").trigger('click');
        return;
      }

      // validate program status conditions
      if (this.$refs.programStatusConditions) {
        if (!await this.$refs.programStatusConditions.validateAll()) {
          $("a[href='#form-sidebar-conditions']").trigger('click');
          return;
        }
      }

      this.saveForm();
    },
    setActiveTab(activeTabTitle) {
      this.activeTab = activeTabTitle;
    },
    setColumnsMapReferral(columnsMap) {
      const email = this.referralDestinationEmail;
      this.columnsMap = columnsMap;
      this.columnsMap.push({
        sourceValue: 'destination_email',
        name: 'destination_email',
        value: email
      })
    },
    onDisplayInOrganizationsInput(input) {
      this.accounts = _.sortBy(input, 'organizationName');
    },
    setProfileMapValidStatus(value) {
      this.profileMapValid = value;
    },
  },
  watch: {
    shareWithParticipant: function (newVal) {
      if (newVal === false) {
        this.schema.forEach(item => {
          if (item.type === 'accordion') {
            item.values.forEach(accordionItem => {
              delete accordionItem.by_participant;
            })
          }
          delete item.by_participant;
        })
      }
    },
    displayInOrganizationsSelect(newVal) {
      if (newVal) {
        if (this.currentOrganization.accountType === 'parent') {
          if (this.childrenAccounts.length === 0) {
            this.fetchChildrenAccounts({accountId: this.currentOrganization.id});
          }
        } else {
          if (this.userAccounts.length === 0) {
            this.loadUserAccounts();
          }
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.form-sidebar-tab .tab-pane {
  height: calc(100vh - 367px);
  overflow: hidden;
  margin-bottom: 10px;
}
#form-map {
  h3 i {
    font-size: 0.8em;
  }
}

.vue-green {
  background: #41b883 !important;
}

.mdi {
  font-size: 1.4em;
}

.mdi-pin, .mdi-pin-off, .mdi-close {
  cursor: pointer;
}

.mdi:before {
  vertical-align: bottom;
}

.portlet-overlay {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  background: #fff;
  opacity: 0.5;
  width: 100%;
  height: 100%;
  z-index: 999;
}

.collection-item {
  cursor: move;
}

.collapsible-header {
  width: 100%;
  cursor: move;
}

.collapsible-body {
  padding: 1rem 2rem !important;
  width: 100%;
  background: #fff;
}

.dragArea li {
  cursor: move;

  & :hover i {
    color: #d09f23;
    cursor: move;
  }
}

i {
  cursor: pointer;

  & :hover {
    color: #d09f23;
  }
}

.sortable-ghost {
  opacity: 0;
}

.states {
  border: 1px solid #ddd;
  padding: 20px;
}

.shared-settings-btn {
  float: right;
  vertical-align: text-bottom;
}

.spacing-button {
  margin-bottom: 10px;

  .dropdown-menu > li {
      > a, > a > i {
      color: #333 !important;
    }
  }
}

</style>



// WEBPACK FOOTER //
// FormBuilder.vue?ba4769be